import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';

/**
 * RotatingTicker Component
 * 
 * Compact horizontal rotating ticker banner (responsive).
 * Features:
 * - Responsive layout for mobile, tablet, and desktop
 * - Auto-rotates every 10 seconds
 * - Pauses on hover (desktop) or touch (mobile)
 * - Smooth horizontal sliding animation (left to right)
 * - Keyboard navigable
 * - ARIA accessible
 */
const RotatingTicker = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  // Card data with headlines, descriptions, CTAs, and icons
  const cards = [
    {
      headline: 'Need Production AI, Not Another Demo?',
      description: 'Book a free AI strategy call for agents, RAG, copilots, and a 90-day execution plan.',
      cta: 'Free AI Call',
      ctaMobile: 'AI Call',
      icon: 'Sparkles',
      action: () => {
        window.open('https://cal.com/octalchip/30min', '_blank');
      }
    },
    {
      headline: 'Launch Generative AI That Pulls Revenue Forward',
      description: 'Ship LLM apps, copilots, and AI-native product features with evals and secure deployment.',
      cta: 'AI Services',
      ctaMobile: 'GenAI',
      icon: 'Bot',
      action: () => {
        window.location.href = '/ai-ml-services';
      }
    },
    {
      headline: 'Manual Work Blocking Team Growth?',
      description: 'Use AI agents and LLM workflows to eliminate repeat tasks and win back execution hours.',
      cta: 'AI Automate',
      ctaMobile: 'Automate',
      icon: 'Zap',
      action: () => {
        window.location.href = '/services/workflow-automation';
      }
    },
    {
      headline: 'Company Knowledge Too Hard to Search?',
      description: 'Deploy enterprise RAG so teams get grounded answers with citations across docs and tools.',
      cta: 'Build RAG',
      ctaMobile: 'RAG',
      icon: 'Database',
      action: () => {
        window.location.href = '/services/ai-ml-services/natural-language-processing';
      }
    },
    {
      headline: 'Email Validation API',
      description: 'Stop paying for bounced campaigns and protect sender reputation with cleaner lists.',
      cta: 'Free Credits',
      ctaMobile: 'Try It',
      icon: 'Mail',
      action: () => {
        window.open('https://mailvalidator.octalchip.com/', '_blank');
      }
    },
    {
      headline: 'QuickSite Business Websites',
      description: 'Go live with a high-converting website stack built for speed, trust, and action.',
      cta: 'Launch Site',
      ctaMobile: 'QuickSite',
      icon: 'LayoutTemplate',
      action: () => {
        window.open('https://quicksite.octalchip.com/', '_blank');
      }
    },
    {
      headline: 'AI Pilot Live But No Business Lift?',
      description: 'Tighten use-case fit, evals, UX, and integrations so your GenAI system converts and retains.',
      cta: 'AI Scale Plan',
      ctaMobile: 'Scale AI',
      icon: 'TrendingUp',
      action: () => {
        window.open('https://cal.com/octalchip/30min', '_blank');
      }
    },
    {
      headline: 'LLM Cost and Quality Spiking in Prod?',
      description: 'Stabilize inference, monitoring, and guardrails with practical cloud AI and LLMOps upgrades.',
      cta: 'LLMOps Review',
      ctaMobile: 'LLMOps',
      icon: 'Cloud',
      action: () => {
        window.location.href = '/services/cloud-devops';
      }
    },
    {
      headline: 'Data Still Trapped Across Tools?',
      description: 'Connect CRM, knowledge bases, and ops systems so AI agents act on one reliable source of truth.',
      cta: 'Integrate AI',
      ctaMobile: 'Integrate',
      icon: 'Plug',
      action: () => {
        window.location.href = '/services/ai-integration';
      }
    },
    {
      headline: 'Need an AI Team That Ships, Not Slides?',
      description: 'See how OctalChip delivers production GenAI with clear communication and accountable timelines.',
      cta: 'Why OctalChip',
      ctaMobile: 'Why Us',
      icon: 'Shield',
      action: () => {
        window.location.href = '/about';
      }
    }
  ];

  // Auto-rotation logic
  useEffect(() => {
    if (!isPaused) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % cards.length);
      }, 10000); // 10 seconds rotation interval
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, cards.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentIndex((prevIndex) => (prevIndex + 1) % cards.length);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentIndex((prevIndex) => (prevIndex - 1 + cards.length) % cards.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cards.length]);

  // Animation variants for horizontal sliding (left to right) - Enhanced for premium feel
  const cardVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 30 : -30, // Subtle slide in
      opacity: 0,
      scale: 0.95
    }),
    center: {
      x: 0, // Center position
      opacity: 1,
      scale: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 30 : -30, // Subtle slide out
      opacity: 0,
      scale: 0.95
    })
  };

  // Smooth transition configuration - Premium easing
  const transition = {
    type: 'spring',
    stiffness: 300,
    damping: 30,
    mass: 0.8
  };

  const currentCard = cards[currentIndex];
  const direction = 1; // Always slide right (left to right) for horizontal rotation

  return (
    <section
      className="hidden md:block w-full bg-gradient-to-br from-[#00d690]/5 via-[#00d690]/8 to-[#00d690]/10 relative z-40 shadow-sm border-b border-[#00d690]/10 backdrop-blur-sm"
      aria-label="Rotating ticker"
      role="region"
    >
      {/* Subtle animated background pattern */}
      <div className="absolute inset-0 opacity-30 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-[#00d690]/5 to-transparent animate-pulse" />
      </div>

      <div
        className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 relative z-10"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
        onFocus={() => setIsPaused(true)}
        onBlur={() => setIsPaused(false)}
      >
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4 lg:gap-6 py-1.5 md:py-2 lg:py-2.5">
          {/* Left side - Icon and Headline */}
          <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 flex-1 min-w-0">
            <div className="flex-shrink-0">
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 400, damping: 17 }}
                className="relative"
              >
                <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 lg:w-12 lg:h-12 bg-gradient-to-br from-[#00d690]/20 to-[#00d690]/10 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md border border-[#00d690]/20 backdrop-blur-sm transition-all duration-300 hover:shadow-lg hover:border-[#00d690]/30">
                  {/* Animated glow effect */}
                  <div className="absolute inset-0 bg-[#00d690]/20 rounded-lg sm:rounded-xl blur-md opacity-0 hover:opacity-100 transition-opacity duration-300 -z-10" />
                  <Icon
                    name={currentCard.icon}
                    size={18}
                    className="sm:w-5 sm:h-5 md:w-5 md:h-5 lg:w-6 lg:h-6 text-[#00d690] drop-shadow-sm"
                  />
                </div>
              </motion.div>
            </div>
            
            <div className="flex-1 min-w-0">
              <AnimatePresence mode="wait" custom={direction}>
                <motion.div
                  key={currentIndex}
                  custom={direction}
                  variants={cardVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={transition}
                  className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-1.5 lg:gap-3"
                >
                  <h2 
                    className="text-sm sm:text-base md:text-lg lg:text-lg xl:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#00d690] to-[#00b876] leading-tight"
                    style={{
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      textOverflow: 'clip',
                      wordBreak: 'break-word',
                      lineHeight: '1.2'
                    }}
                  >
                    {currentCard.headline}
                  </h2>
                  <span 
                    className="text-xs lg:text-sm text-gray-700 font-medium leading-snug hidden lg:inline"
                  >
                    {currentCard.description}
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Right side - CTA Button */}
          <div className="flex-shrink-0">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.button
                key={`cta-${currentIndex}`}
                custom={direction}
                variants={cardVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
                onClick={currentCard.action}
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.98 }}
                className="group relative bg-gradient-to-r from-[#00d690] to-[#00b876] text-white px-3 sm:px-3.5 md:px-4 lg:px-5 py-1.5 sm:py-2 md:py-2 lg:py-2.5 text-xs sm:text-sm lg:text-sm font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-[#00d690] focus:ring-offset-2 focus:ring-offset-white btn-animate-bounce"
                aria-label={`${currentCard.cta} - ${currentCard.headline}`}
              >
                {/* Shine effect on hover */}
                <div className="absolute inset-0 rounded-full overflow-hidden pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-in-out" />
                </div>
                
                {/* Button content — shorter label below lg so text never clips */}
                <span className="relative z-10 flex items-center gap-1 sm:gap-1.5">
                  <span className="hidden lg:inline">{currentCard.cta}</span>
                  <span className="lg:hidden">{currentCard.ctaMobile || currentCard.cta}</span>
                  <Icon 
                    name="ArrowRight" 
                    size={12}
                    className="sm:w-3.5 sm:h-3.5 lg:w-4 lg:h-4 transition-transform duration-300 group-hover:translate-x-0.5" 
                  />
                </span>
              </motion.button>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RotatingTicker;

